import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, X } from '@/components/icons';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/components/ui/useToast';
import { usePrompt } from '@/components/ui/usePrompt';
import { useAuth } from '@/auth/useAuth';
import { apiError } from '@/lib/apiError';
import { listGrnHolds, overrideGrnHold, cancelGrnHold, type OutwardGrn } from '@/api/outwardGrn';

const fmt = (v: string | number | null | undefined, d = 3) =>
  v == null || v === '' ? '—' : Number(v).toLocaleString('en-IN', { maximumFractionDigits: d });

/** Variance % = (actual − expected) ÷ average of the two × 100 — same formula as the
 *  backend, recomputed here so holds raised before the formula change read the same. */
function varianceOf(g: OutwardGrn): number | null {
  const e = Number(g.expectedNetKg), a = Number(g.actualNetKg);
  if (g.expectedNetKg != null && g.actualNetKg != null && e + a > 0) return ((a - e) / ((a + e) / 2)) * 100;
  return g.variancePct != null ? Number(g.variancePct) : null;
}

function Stat({ label, value, tone }: { label: string; value: React.ReactNode; tone?: 'danger' }) {
  return (
    <div className="min-w-0 rounded-md bg-[var(--bg-secondary)] px-2.5 py-1.5">
      <div className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">{label}</div>
      <div className={`truncate text-sm font-extrabold tabular-nums ${tone === 'danger' ? 'text-danger' : 'text-[var(--text-primary)]'}`}>{value}</div>
    </div>
  );
}

/** Weight-hold override queue — shown to Director / Super Admin only. */
export function WeightHoldsPanel() {
  const qc = useQueryClient();
  const toast = useToast();
  const ask = usePrompt();
  const { user } = useAuth();
  const canOverride = user?.roleCode === 'DIRECTOR' || user?.roleCode === 'SUPER_ADMIN';

  const query = useQuery({
    queryKey: ['outward-grn', 'holds'],
    queryFn: listGrnHolds,
    refetchInterval: 60_000,
    enabled: canOverride,
  });
  const holds = query.data ?? [];

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['outward-grn'] });
  };
  const overrideMut = useMutation({
    mutationFn: ({ id, note }: { id: number; note?: string }) => overrideGrnHold(id, note),
    onSuccess: (g) => {
      toast.push(`${g.grnNo} overridden — goods dispatched.`, 'success');
      refresh();
    },
    onError: (e) => toast.push(apiError(e, 'Override failed'), 'error'),
  });
  const cancelMut = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => cancelGrnHold(id, reason),
    onSuccess: (g) => {
      toast.push(`${g.grnNo} cancelled.`, 'info');
      refresh();
    },
    onError: (e) => toast.push(apiError(e, 'Cancel failed'), 'error'),
  });

  async function onOverride(g: OutwardGrn) {
    const note = await ask({
      title: `Override weight hold — ${g.grnNo}?`,
      message: `Variance ${fmt(varianceOf(g), 1)}%. Goods will dispatch as normal.`,
      label: 'Override note',
      placeholder: 'Optional',
      multiline: true,
      required: false,
      confirmLabel: 'Override',
    });
    if (note === null) return; // cancelled the prompt
    overrideMut.mutate({ id: g.id, note: note.trim() || undefined });
  }
  async function onCancel(g: OutwardGrn) {
    const reason = await ask({ title: `Cancel held GRN ${g.grnNo}?`, label: 'Reason', multiline: true, tone: 'danger', confirmLabel: 'Cancel GRN' });
    if (!reason?.trim()) return;
    cancelMut.mutate({ id: g.id, reason: reason.trim() });
  }

  if (!canOverride || holds.length === 0) return null;

  return (
    <div className="mb-4 rounded-xl border border-warning/40 bg-warning/[0.06] p-3 sm:p-4">
      <div className="mb-2.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
        <span className="flex items-center gap-2 text-sm font-extrabold text-warning">
          <AlertTriangle size={16} />
          Weight Holds awaiting override ({holds.length})
        </span>
        <span className="text-[11px] text-[var(--text-muted)]">Variance = (Actual − Expected) ÷ Average net × 100</span>
      </div>
      <div className="flex flex-col gap-2">
        {holds.map((g) => {
          const v = varianceOf(g);
          const diff = g.expectedNetKg != null && g.actualNetKg != null ? Number(g.actualNetKg) - Number(g.expectedNetKg) : null;
          const under = (v ?? 0) < 0;
          const customer = g.onlineCustomer ?? g.party?.name ?? '—';
          return (
            <div key={g.id} className="rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] p-2.5 sm:p-3">
              <div className="flex flex-col gap-2.5 lg:flex-row lg:items-center lg:gap-4">
                <div className="min-w-0 lg:w-64 lg:shrink-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-sm font-extrabold text-accent">{g.grnNo}</span>
                    {v != null && (
                      <span className="rounded-full bg-danger/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-danger">
                        {under ? 'Under-weight' : 'Over-weight'}
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 truncate text-xs text-[var(--text-muted)]">
                    {g.dispatchOrder?.doNo ?? '—'} · {customer}
                  </div>
                </div>
                <div className="grid flex-1 grid-cols-2 gap-1.5 sm:grid-cols-4">
                  <Stat label="Expected net" value={`${fmt(g.expectedNetKg)} kg`} />
                  <Stat label="Actual net" value={`${fmt(g.actualNetKg)} kg`} />
                  <Stat label="Difference" value={diff == null ? '—' : `${diff > 0 ? '+' : diff < 0 ? '−' : ''}${fmt(Math.abs(diff))} kg`} />
                  <Stat label="Variance" tone="danger" value={v == null ? '—' : `${v > 0 ? '+' : v < 0 ? '−' : ''}${fmt(Math.abs(v), 1)}%`} />
                </div>
                <div className="flex shrink-0 gap-1.5 [&>*]:flex-1 sm:justify-end sm:[&>*]:flex-none">
                  <Button variant="success" size="sm" onClick={() => onOverride(g)} disabled={overrideMut.isPending}>
                    <span className="inline-flex items-center gap-1"><Check size={13} /> Override</span>
                  </Button>
                  <Button variant="danger" size="sm" onClick={() => onCancel(g)} disabled={cancelMut.isPending}>
                    <span className="inline-flex items-center gap-1"><X size={13} /> Cancel</span>
                  </Button>
                </div>
              </div>
              {(g.weighbridgeGrossKg != null || g.holdReason) && (
                <div className="mt-2 flex flex-wrap gap-x-3 gap-y-0.5 border-t border-[var(--border-color)] pt-1.5 text-[11px] text-[var(--text-muted)]">
                  {g.weighbridgeGrossKg != null && <span>Packed gross {fmt(g.weighbridgeGrossKg)} kg (box included)</span>}
                  {g.holdReason && <span className="italic">Reason: {g.holdReason}</span>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
